import React from 'react'
import { NumericFormat } from 'react-number-format'
import { Field, ErrorMessage, FastField, useFormikContext } from 'formik'
import isEmpty from 'lodash/isEmpty'
import Box from '@material-ui/core/Box'
import Tooltip from '@material-ui/core/Tooltip'

import { R } from '../../utils/currency'

/*
  * @prop {string} name - Name of the form input
  * @prop {string} Placeholder - Placeholder text for the empty or initial state
  * @prop {string} className - Additional classname to add to the component
  * @prop {boolean} disabled - Disable the input box
 */
export const NumberField = ({ name, placeholder, className, disabled }) =>
  <div className={`form-field app-form__field ${className ? className : ''}`}>
    <Field
      disabled={disabled}
      name={name}
      component={NumberInput}
      placeholder={placeholder}
    />
    <ErrorMessage component='span' name={name} />
  </div>

/*
  * @prop {string} name - Name of the form input
  * @prop {string} Placeholder - Placeholder text for the empty or initial state
  * @prop {string} className - Additional classname to add to the component
  * @prop {boolean} disabled - Disable the input box
 */
export const FastNumberField = ({ name, placeholder, className, disabled }) =>
  <div className={`form-field app-form__field ${className ? className : ''}`}>
    <FastField disabled={disabled} name={name} component={NumberInput} placeholder={placeholder} />
  </div>

/*
  * @prop {string[]} autoFillFieldNames - is an optional prop that auto fills fields
  * with the names specified in autoFillFieldNames to the value of this
  * field.
  * @prop {string[]} touchFields - is an optional prop that touches other fields when this field is changed
  * @prop {string} name - Name of the form input
  * @prop {string} label - String for the input label
  * @prop {string} subtext - Subtext that appears under the input box
  * @prop {boolean} disabled - Disable the input box
  * @prop {string} placeholder - Placeholder text for empty state
  * @prop {boolean} readOnly
  * @prop {string} value
  * @prop {Object} style - React style object to override the component styles
  * @prop {boolean} withoutError - Disable the error message
  * @prop {string} inputTooltip - Tooltip to show when hovering over the input box
  * @prop {function} onChange - Function to call when the input changes
  * @prop {number} precision - Number of decimal places to round to
  * @prop {Object} numberFormatProps - Props to pass to the NumberFormat component 
  *   see: https://s-yadav.github.io/react-number-format/docs/intro
 */
export const NumberFieldWithLabel = ({
  name, label, value, placeholder, precision,
  disabled, readOnly, withoutError,
  inputTooltip,
  subtext,
  style,
  touchFields, autoFillFieldNames,
  onChange: propsOnchange,
  numberFormatProps
}) => {
  const { setFieldValue, setFieldTouched } = useFormikContext()
  const onChange = (e) => {
    const { value } = e.target
    if (propsOnchange) {
      propsOnchange(e)
    } else if (precision) {
      setFieldValue(name, R(value, { precision }).value)
    } else {
      setFieldValue(name, R(value).value)
    }
    if (!isEmpty(autoFillFieldNames)) {
      autoFillFieldNames.forEach((name) => setFieldValue(name, value))
    }
    if (!isEmpty(touchFields)) {
      touchFields.forEach((name) => setFieldTouched(name))
    }
  }
  return (
    <div style={style} className={`form-field app-form__field ${subtext ? 'app-form__field--has-subtext' : ''}`}>
      <label>{label}</label>
      <Tooltip title={inputTooltip || ''}>
        <Box>
          {value
            ? <Field
              name={name} value={value}
              disabled={disabled} readOnly={readOnly}
              onChange={onChange}
              component={NumberInput} numberFormatProps={numberFormatProps}
              placeholder={placeholder} />
            : <Field
              name={name}
              disabled={disabled} readOnly={readOnly}
              onChange={onChange}
              component={NumberInput} numberFormatProps={numberFormatProps}
              placeholder={placeholder} />
          }
        </Box>
      </Tooltip>
      {subtext &&
        <small className='subtext'>{subtext}</small>
      }
      {!withoutError &&
        <ErrorMessage component='span' name={name} />
      }
    </div>
  )
}

const NumberInput = (props) => {
  return (
    <NumericFormat
      valueIsNumericString
      allowNegative={false}
      thousandSeparator={' '}
      {...props.numberFormatProps}
      {...props.field}
      {...props}
    />
  )
}
